import React from 'react';
import PropTypes from 'prop-types';
import * as turf from '@turf/turf';

import { compose } from 'redux';
import { connect } from 'react-redux';
import {
    cancelComparableAsync,
    startComparableAsync,
    exportComparableAsync
} from '../../../../redux/comparable/Actions';

import withStyles from '@material-ui/core/styles/withStyles';
import { Button, Tooltip, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';

import ErrorPropType from '../../../../utility/prop_types/ErrorPropType';
import InfoCardList from '../common/InfoCardList';
import InfoCard from '../common/InfoCard';
import AreaInfoCard from '../common/AreaInfoCard';

import Progress from '../../../Common/UI/Progress';

const areaUnits = ['sqft', 'm2'];
class ComparableActions extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        // Redux State
        geojson: PropTypes.object,
        criteria: PropTypes.object.isRequired,
        comparable: PropTypes.object,
        isComparableDirty: PropTypes.bool.isRequired,
        isRunning: PropTypes.bool.isRequired,
        error: ErrorPropType,
        maxArea: PropTypes.number.isRequired,
        isExportAllowed: PropTypes.bool.isRequired,
        // Redux Dispatch
        dispatchStartComparableAsync: PropTypes.func.isRequired,
        dispatchCancelComparableAsync: PropTypes.func.isRequired,
        dispatchExportComparableAsync: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.runComparables = this.runComparables.bind(this);
        this.cancelComparables = this.cancelComparables.bind(this);
        this.export = this.export.bind(this);
        this.validateComparables = this.validateComparables.bind(this);
        this.formatValue = this.formatValue.bind(this);
    }

    runComparables() {
        const { geojson, criteria, dispatchStartComparableAsync } = this.props;
        if (this.validateComparables()) {
            dispatchStartComparableAsync(geojson.geometry, criteria);
        }
    }

    cancelComparables() {
        const { comparable, dispatchCancelComparableAsync } = this.props;
        if (comparable) {
            dispatchCancelComparableAsync(comparable.id);
        }
    }

    validateComparables() {
        const { geojson, maxArea, criteria } = this.props;
        if (!geojson) {
            return false;
        }
        if (turf.area(geojson.geometry) > maxArea) {
            return false;
        }
        return true;
    }

    export() {
        const { comparable, dispatchExportComparableAsync } = this.props;
        if (comparable) {
            dispatchExportComparableAsync(comparable.id);
        }
    }

    formatValue(value, min, max) {
        return value.toLocaleString(undefined, {
            minimumFractionDigits: min,
            maximumFractionDigits: max
        });
    }

    render() {
        const {
            classes,
            comparable,
            error,
            isRunning,
            isComparableDirty,
            isExportAllowed,
            defaultAreaUnit
        } = this.props;

        const isValidComparables = this.validateComparables();
        const isValidResult = Boolean(
            !error &&
                comparable &&
                comparable.computedData &&
                comparable.status === 'complete'
        );
        const data = comparable && comparable.computedData;

        return (
            <>
                {isValidResult && data.uprns.length > 0 && (
                    <Typography variant="caption" color="textPrimary">
                        Averages calculated from <b>{data.uprns.length}</b>{' '}
                        properties with average price of{' '}
                        <b>£ {this.formatValue(data.priceAvg, 0, 0)}</b>
                    </Typography>
                )}
                {/* Commented out progress bar because it aint very helpful and results return pretty fast anyway */}
                {/* {
                    comparable && ['running', 'queued', 'ready'].includes(comparable.status) && <Progress
                        progress={comparable.progress || 0}
                        status={comparable.status}
                        remainingTime={comparable.remainingTime}
                    />
                } */}
                {(isRunning || isValidResult) && (
                    <InfoCardList>
                        <InfoCard skeleton={isRunning}>
                            <Typography>
                                Price Sold
                                <Tooltip
                                    arrow
                                    title={
                                        'Average £/sqft of the properties when they were last sold (where EPC data is available)'
                                    }
                                    placement={'right'}>
                                    <InfoOutlinedIcon />
                                </Tooltip>
                            </Typography>
                            <Typography>
                                {/* <PlaceholderIcon/> */}
                                {isValidResult && data.ppsqftAvg
                                    ? `£ ${this.formatValue(data.ppsqftAvg, 2, 2)}`
                                    : 'N/A'}
                            </Typography>
                        </InfoCard>
                        <InfoCard skeleton={isRunning}>
                            <Typography>
                                Market Value
                                <Tooltip
                                    arrow
                                    title={
                                        'Average £/sqft of the properties modified by HPI change data (where EPC data is available)'
                                    }
                                    placement={'right'}>
                                    <InfoOutlinedIcon />
                                </Tooltip>
                            </Typography>
                            {isValidResult && data.marketValueAvg ? (
                                <Typography
                                    className={
                                        data.marketValueAvg > data.ppsqftAvg
                                            ? classes.valueIncrease
                                            : classes.valueDecrease
                                    }>
                                    {/* <PlaceholderIcon/> */}
                                    {`£ ${this.formatValue(
                                        data.marketValueAvg,
                                        2,
                                        2
                                    )}`}
                                    {data.marketValueAvg > data.ppsqftAvg ? (
                                        <ArrowUpwardIcon />
                                    ) : (
                                        <ArrowDownwardIcon />
                                    )}
                                </Typography>
                            ) : (
                                <Typography>N/A</Typography>
                            )}
                        </InfoCard>
                    </InfoCardList>
                )}
                {(isRunning || isValidResult) && (
                    <InfoCardList>
                        <AreaInfoCard
                            skeleton={isRunning}
                            value={isValidResult ? data.floorAreaAvg : undefined}
                            label="Floor Area"
                            defaultAreaUnit={
                                areaUnits.includes(defaultAreaUnit)
                                    ? defaultAreaUnit
                                    : 'sqft'
                            }
                            areaUnits={areaUnits}
                            labelProps={{ style: { fontSize: 14 } }}
                        />
                        <InfoCard skeleton={isRunning}>
                            <Typography>Building Height</Typography>
                            <Typography>
                                {/* <PlaceholderIcon/> */}
                                {isValidResult && data.buildingHeightAvg
                                    ? `${this.formatValue(
                                          data.buildingHeightAvg,
                                          0,
                                          2
                                      )} m`
                                    : 'N/A'}
                            </Typography>
                        </InfoCard>
                        <InfoCard skeleton={isRunning}>
                            <Typography>
                                Population
                                <Tooltip
                                    arrow
                                    title={
                                        'Population of the area based on the percentage of Lower Super Output Area intersecting the search area'
                                    }
                                    placement={'right'}>
                                    <InfoOutlinedIcon />
                                </Tooltip>
                            </Typography>
                            <Typography>
                                {/* <PlaceholderIcon/> */}
                                {isValidResult && data.lsoaPopulationSum
                                    ? `${this.formatValue(
                                          data.lsoaPopulationSum,
                                          0,
                                          0
                                      )}`
                                    : 'N/A'}
                            </Typography>
                        </InfoCard>
                    </InfoCardList>
                )}
                {comparable &&
                    comparable.status === 'complete' &&
                    data &&
                    data.uprns.length === 0 && (
                        <Alert
                            icon={false}
                            severity={'error'}
                            className={classes.alertRoot}>
                            <AlertTitle>
                                {
                                    'No properties where found. Please refine your search by specifying a different area, or adjusting your filters'
                                }
                            </AlertTitle>
                        </Alert>
                    )}
                {error && (
                    <Alert
                        icon={false}
                        severity={'error'}
                        className={classes.alertRoot}>
                        <Typography variant="subtitle1">
                            {error.status} {error.statusText}
                        </Typography>
                        <Typography variant="body2">{error.data}</Typography>
                    </Alert>
                )}
                <div className={classes.actionsRoot}>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        disabled={
                            !isRunning && (!isComparableDirty || !isValidComparables)
                        }
                        onClick={
                            isRunning ? this.cancelComparables : this.runComparables
                        }>
                        {isRunning ? 'Cancel' : ' Calculate'}
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        disabled={
                            !(
                                isExportAllowed &&
                                isValidResult &&
                                data.uprns.length > 0
                            )
                        }
                        onClick={this.export}>
                        {isExportAllowed
                            ? 'Export CSV'
                            : 'Export CSV (Coming Soon!)'}
                    </Button>
                </div>
            </>
        );
    }
}

const style = theme => ({
    actionsRoot: {
        display: 'flex',
        '& > *': {
            flex: '1 1 0',
            '&:not(:last-child)': {
                marginRight: 12
            }
        }
    },
    alertRoot: {
        padding: '10px'
    },
    valueIncrease: {
        color: theme.palette.success.main
    },
    valueDecrease: {
        color: theme.palette.error.main
    }
});

const mapStateToProps = state => {
    return {
        geojson: state.comparableReducer.comparableArea.geojson,
        error: state.comparableReducer.error,
        criteria: state.comparableReducer.comparableArea.criteria,
        isRunning: state.comparableReducer.isRunning,
        comparable: state.comparableReducer.comparable,
        isComparableDirty: state.comparableReducer.isComparableDirty,
        maxArea: state.user.features['comparable']
            ? state.user.features['comparable'].maxArea || 0
            : 0,
        isExportAllowed: state.user.features['comparable']
            ? state.user.features['comparable'].export || false
            : false,
        defaultAreaUnit: state.user?.prefs?.defaultAreaUnit ?? 'sqft'
    };
};

const mapDispatchToProps = dispatch => ({
    dispatchStartComparableAsync: (geojson, criteria) =>
        dispatch(startComparableAsync(geojson, criteria)),
    dispatchCancelComparableAsync: id => dispatch(cancelComparableAsync(id)),
    dispatchExportComparableAsync: id => dispatch(exportComparableAsync(id))
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withStyles(style)
)(ComparableActions);
